import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, ArrowLeft, Check, Phone, MapPin, ChevronRight, MessageCircle } from 'lucide-react';
import { trackPageView, trackProductView, trackCTAClick } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/products/${productId}`)
      .then(res => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(data => {
        setProduct(data);
        trackPageView(`/products/${productId}`, data.name);
        trackProductView(productId, data.name);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        setLoading(false);
      });
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-2 border-coral border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-500 font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold text-white mb-4">Product Not Found</h1>
          <Link to="/products" className="text-coral hover:underline">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void" data-testid="product-detail-page">
      {product && (
        <SEOHead
          title={product.name}
          description={`${product.name} — ${product.tagline}. ${product.description?.substring(0, 140)}...`}
          path={`/products/${productId}`}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "brand": { "@type": "Brand", "name": "Avi Cement" },
            "manufacturer": { "@type": "Organization", "name": "Avi Cement Limited" },
            "url": `https://avicement.com/products/${productId}`,
            "category": product.category,
          }}
        />
      )}
      {/* Hero Section */}
      <section className="relative py-12 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        
        {/* Gradient based on product color */}
        <motion.div
          className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full"
          style={{ 
            background: `radial-gradient(circle, ${product.color}15 0%, transparent 60%)` 
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link 
              to="/products" 
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white font-body text-sm mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Products
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Category Badge */}
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ backgroundColor: `${product.color}20` }}
              >
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: product.color }}
                />
                <span 
                  className="font-mono text-sm"
                  style={{ color: product.color }}
                >
                  {product.category}
                </span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                {product.name}
              </h1>
              
              <p 
                className="font-display text-xl font-medium mb-6"
                style={{ color: product.color }}
              >
                {product.tagline}
              </p>
              
              <p className="text-gray-400 text-lg leading-relaxed mb-10">
                {product.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  onClick={() => trackCTAClick('request_quote', 'product_detail')}
                  data-testid="product-cta-quote"
                  className="group flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-8 py-4 rounded-full transition-all"
                >
                  Request a Quote
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/locations"
                  onClick={() => trackCTAClick('find_dealer', 'product_detail')}
                  data-testid="product-cta-dealer"
                  className="group flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium px-8 py-4 rounded-full transition-all"
                >
                  <MapPin className="w-5 h-5" /> Find a Dealer
                </Link>
                <a
                  href={`https://wa.me/918140044100?text=Hi, mujhe ${product?.name || 'your product'} ke baare mein jaankari chahiye. Please pricing aur availability batayein.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-whatsapp-btn"
                  className="group flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-display font-medium px-8 py-4 rounded-full transition-all"
                >
                  <MessageCircle className="w-5 h-5" /> WhatsApp Quote
                </a>
              </div>
            </motion.div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center gap-4"
            >
              {product.image ? (
                <>
                  <div 
                    className="w-64 h-64 md:w-80 md:h-80 rounded-3xl flex items-center justify-center overflow-hidden"
                    style={{ 
                      background: `linear-gradient(135deg, ${product.color}20 0%, ${product.color}08 100%)`,
                      border: `1px solid ${product.color}20`
                    }}
                  >
                    <img 
                      src={product.image} 
                      alt={`${product.name} - ${product.tagline || product.category} cement bag by Avi Cement Limited`} 
                      loading="lazy"
                      width="400"
                      height="400"
                      className="w-full h-full object-contain p-4"
                      data-testid="product-main-image"
                    />
                  </div>
                  {product.images && product.images.length > 1 && (
                    <div className="flex gap-3">
                      {product.images.map((img, i) => (
                        <div 
                          key={i}
                          className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border border-white/10 hover:border-white/30 transition-all cursor-pointer"
                          style={{ background: `${product.color}10` }}
                        >
                          <img src={img} alt={`${product.name} view ${i + 1}`} loading="lazy" width="80" height="80" className="w-full h-full object-contain p-1" />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div 
                  className="w-64 h-64 md:w-80 md:h-80 rounded-3xl flex items-center justify-center"
                  style={{ 
                    background: `linear-gradient(135deg, ${product.color}30 0%, ${product.color}10 100%)`,
                    border: `1px solid ${product.color}30`
                  }}
                >
                  <div 
                    className="w-24 h-24 md:w-32 md:h-32 rounded-2xl"
                    style={{ backgroundColor: product.color }}
                  />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section ref={ref} className="py-12 md:py-32 bg-surface">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Specifications */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <span className="text-coral font-mono text-sm mb-4 block">SPECIFICATIONS</span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">
                Technical Details
              </h2>
              
              <div className="space-y-4">
                {product.specifications?.map((spec, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4 bg-void/50 border border-white/5 p-4 rounded-xl"
                  >
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${product.color}20` }}
                    >
                      <Check className="w-4 h-4" style={{ color: product.color }} />
                    </div>
                    <span className="font-mono text-sm text-gray-300">{spec}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Grade Variants (for Hathi/Siddhee) */}
            {product.sub_categories?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <span className="text-coral font-mono text-sm mb-4 block">AVAILABLE GRADES</span>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">
                  Choose Your Grade
                </h2>
                <div className="space-y-4">
                  {product.sub_categories.map((variant, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: index * 0.12 }}
                      className="bg-void/50 border border-white/5 p-5 rounded-xl"
                      data-testid={`grade-variant-${index}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className="px-3 py-1 rounded-lg text-xs font-semibold"
                          style={{ backgroundColor: `${product.color}20`, color: product.color }}
                        >
                          {variant.grade}
                        </div>
                        <span className="text-gray-500 font-mono text-xs">{variant.standard}</span>
                      </div>
                      <p className="text-white text-sm font-medium mb-1">Strength: {variant.strength}</p>
                      <p className="text-gray-400 text-xs">Best for: {variant.best_for}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Use Cases */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-coral font-mono text-sm mb-4 block">APPLICATIONS</span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">
                Ideal Use Cases
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {product.use_cases?.map((useCase, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: index * 0.1 }}
                    className="bg-void/50 p-4 rounded-xl border-l-2"
                    style={{ borderLeftColor: product.color }}
                  >
                    <span className="text-gray-300 text-sm">{useCase}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-void border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                Need expert advice?
              </h3>
              <p className="text-gray-400">
                Our team is ready to help with your project requirements.
              </p>
            </div>
            <a
              href="tel:+918140044100"
              className="flex items-center gap-3 bg-surface border border-white/10 hover:border-white/30 text-white font-display px-6 py-4 rounded-full transition-all"
            >
              <Phone className="w-5 h-5 text-coral" />
              +91 8140 044100
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;
